import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Course, CourseRating, Competition } from '../types/course';
import { MetrixCourseDetailService } from '../services/metrixCourseDetailService';
import { CourseRatingService, extractUserNameFromEmail } from '../services/CourseRatingService';
import { LocationService } from '../services/LocationService';
import { CourseDataService } from '../services/CourseDataService';
import { useAuth } from './AuthContext';

/**
 * Tarkistaa onko karttarajojen muutos riittävän merkittävä, jotta suodatus kannattaa päivittää.
 * Pienet muutokset (alle kynnysarvon) ohitetaan turhien suodatusten välttämiseksi.
 * 
 * @param oldBounds Edelliset karttarajat
 * @param newBounds Uudet karttarajat
 * @param threshold Kynnysarvo muutoksen merkittävyydelle (asteissa)
 * @returns true jos muutos on merkittävä, muuten false
 */
function isSignificantBoundsChange(
  oldBounds: google.maps.LatLngBounds | null,
  newBounds: google.maps.LatLngBounds,
  threshold: number = 0.01 // 0.01 astetta ~ 1km
): boolean {
  // Jos aiempia rajoja ei ole, muutos on aina merkittävä
  if (!oldBounds) return true;
  
  // Haetaan rajojen koordinaatit
  const oldNE = oldBounds.getNorthEast();
  const oldSW = oldBounds.getSouthWest();
  const newNE = newBounds.getNorthEast();
  const newSW = newBounds.getSouthWest();
  
  // Lasketaan koordinaattien erot
  const latDiffNE = Math.abs(oldNE.lat() - newNE.lat());
  const lngDiffNE = Math.abs(oldNE.lng() - newNE.lng());
  const latDiffSW = Math.abs(oldSW.lat() - newSW.lat());
  const lngDiffSW = Math.abs(oldSW.lng() - newSW.lng());
  
  // Muutos on merkittävä, jos mikä tahansa koordinaatti on muuttunut yli kynnysarvon
  return latDiffNE > threshold || 
         lngDiffNE > threshold || 
         latDiffSW > threshold || 
         lngDiffSW > threshold;
}

// Define the shape of our context
interface CourseContextType {
  // Layout hole count state
  layoutHoleCounts: {[key: string]: number};
  loadingLayouts: {[key: string]: boolean};
  fetchLayoutHoleCount: (layoutId: string, forceRefresh?: boolean) => Promise<void>;
  loadVisibleLayoutHoleCounts: (layoutIds: string[]) => Promise<void>;
  getTotalHoleCount: (course: Course) => number;
  
  // NEW: Clustered markers tracking
  clusteredMarkerIds: string[];
  setClusteredMarkerIds: (ids: string[]) => void;
  updateClusteredMarkers: (clusteredMarkers: string[]) => void;
  isMarkerClustered: (markerId: string) => boolean;
  
  // Course state
  courses: Course[];
  visibleCourses: Course[];
  setVisibleCourses: (courses: Course[]) => void;
  selectedCourse: Course | null;
  selectCourse: (course: Course | null) => void;
  setSelectedCourse: (course: Course | null) => void; // Direct setter to bypass centering behavior
  clearSelectedCourse: () => void; // Added function to clear selected course
  
  // Course filtering
  filterCoursesByBounds: (courses: Course[], bounds: google.maps.LatLngBounds) => Course[];
  filterCoursesBySearch: (courses: Course[], searchQuery: string) => Course[];
  
  // Competition/Event filtering
  visibleCompetitions: Competition[];
  setVisibleCompetitions: (competitions: Competition[]) => void;
  filterCompetitionsByBounds: (competitions: Competition[], bounds: google.maps.LatLngBounds) => Competition[];
  filterCompetitionsBySearch: (competitions: Competition[], searchQuery: string) => Competition[];
  eventDateRange: number;
  setEventDateRange: (range: number) => void;
  eventShowPdgaOnly: boolean;
  setEventShowPdgaOnly: (value: boolean) => void;
  
  // Location functionality
  userLocation: { lat: number; lng: number } | null;
  setUserLocation: (location: { lat: number; lng: number } | null) => void;
  updateUserLocation: (location: { lat: number; lng: number } | null) => void;
  calculateDistanceToCourse: (course: Course) => number;
  formatDistance: (distance: number) => string;
  isLocationLoading: boolean;
  requestUserLocation: () => void; // Function to explicitly request user location
  centerToUserLocation: boolean;
  setCenterToUserLocation: (center: boolean) => void;
  isUsingActualUserLocation: boolean; // Flag to track if using actual user location vs default
  centerToCourseLocation: { lat: number; lng: number } | null;
  setCenterToCourseLocation: (location: { lat: number; lng: number } | null) => void;
  
  // Map bounds and navigation
  mapBounds: google.maps.LatLngBounds | null;
  updateMapBounds: (bounds: google.maps.LatLngBounds) => void;
  mapZoom: number | null; // Current map zoom level
  updateMapZoom: (zoom: number) => void; // Update map zoom level
  
  // User data management
  clearUserData: () => void; // Add function to clear user-specific data when account is deleted
  
  // Course ratings
  courseRatings: {[courseId: string]: CourseRating[]};
  courseAverageRatings: {[courseId: string]: number};  // Lisätty keskiarvot
  courseColorRatings: {[courseId: string]: 'green' | 'yellow' | 'red' | undefined};  // Lisätty värikoodit
  loadingRatings: boolean;
  loadCourseRatings: (courseId: string) => void;  // Lisätty tietoja lataava metodi
  ratingError: string | null;
  ratingSuccess: string | null;
  submitCourseRating: (courseId: string, rating: 'green' | 'yellow' | 'red', comment: string) => Promise<void>; // Korjattu metodin nimi
  deleteRating: (ratingId: string) => Promise<void>;
  clearRatingMessages: () => void;
  calculateAverageRating: (ratings: CourseRating[]) => number;
  
  // Course data
  competitions: any[];
  isDataLoading: boolean;
}

// Create the context with undefined default value
const CourseContext = createContext<CourseContextType | undefined>(undefined);

// Props for our provider component
interface CourseProviderProps {
  children: React.ReactNode;
}

// Provider component that will wrap the app and provide the course context
export const CourseProvider: React.FC<CourseProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // Ref to track active rating listeners
  const activeRatingListenersRef = useRef<Map<string, () => void>>(new Map());
  
  // State for layout hole counts
  const [layoutHoleCounts, setLayoutHoleCounts] = useState<{[key: string]: number}>({});
  const [loadingLayouts, setLoadingLayouts] = useState<{[key: string]: boolean}>({});
  
  // NEW: State to track which markers are currently clustered
  const [clusteredMarkerIds, setClusteredMarkerIds] = useState<string[]>([]);
  
  // State for application readiness and hole count loading
  const [isAppReady, setIsAppReady] = useState<boolean>(false);
  const [shouldLoadHoleCounts, setShouldLoadHoleCounts] = useState<boolean>(false);
  
  // State for course selection
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  
  // State for visible courses and competitions
  const [visibleCourses, setVisibleCourses] = useState<Course[]>([]);
  const [visibleCompetitions, setVisibleCompetitions] = useState<Competition[]>([]);
  
  // State for user location
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLocationLoading, setIsLocationLoading] = useState<boolean>(false);
  const [centerToUserLocation, setCenterToUserLocation] = useState<boolean>(false);
  const [isUsingActualUserLocation, setIsUsingActualUserLocation] = useState<boolean>(false);
  
  // State for course-related map actions
  const [centerToCourseLocation, setCenterToCourseLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // State for map bounds and zoom
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [mapZoom, setMapZoom] = useState<number | null>(null);
  
  // Update map zoom level
  const updateMapZoom = useCallback((zoom: number) => {
    setMapZoom(zoom);
  }, []);
  
  // State for course ratings
  const [courseRatings, setCourseRatings] = useState<{[courseId: string]: CourseRating[]}>({});
  const [courseAverageRatings, setCourseAverageRatings] = useState<{[courseId: string]: number}>({});
  const [courseColorRatings, setCourseColorRatings] = useState<{[courseId: string]: 'green' | 'yellow' | 'red' | undefined}>({});
  const [loadingRatings, setLoadingRatings] = useState<boolean>(false);
  const [ratingError, setRatingError] = useState<string | null>(null);
  const [ratingSuccess, setRatingSuccess] = useState<string | null>(null);
  
  // State for course data
  const [courses, setCourses] = useState<Course[]>([]);
  const [competitions, setCompetitions] = useState<any[]>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [eventDateRange, setEventDateRange] = useState<number>(30);
  const [eventShowPdgaOnly, setEventShowPdgaOnly] = useState<boolean>(false);
  
  // Refs
  const mapBoundsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingBatch = useRef<boolean>(false);
  const lastProcessedBoundsRef = useRef<google.maps.LatLngBounds | null>(null);
  // Huom: pendingFetches on jo määritelty muualla tässä tiedostossa
  
  // CustomEvent-tapahtumille kuuntelija (CourseSidebarV2 -> CourseContext synkronointi)
  useEffect(() => {
    // Määritellään tapahtumankäsittelijä
    const handleLayoutHoleCountUpdate = (event: any) => {
      const { courseId, holeCount } = event.detail;
      if (courseId && typeof holeCount === 'number') {
        // Päivitetään layoutHoleCounts kun saadaan väylämäärätieto
        setLayoutHoleCounts(prev => ({
          ...prev,
          [courseId]: holeCount
        }));
      }
    };

    // Rekisteröidään tapahtumankäsittelijä
    window.addEventListener('updateLayoutHoleCounts', handleLayoutHoleCountUpdate);

    // Poistetaan tapahtumankäsittelijä siivousoperaatiossa
    return () => {
      window.removeEventListener('updateLayoutHoleCounts', handleLayoutHoleCountUpdate);
    };
  }, []);
  
  // Function to select a course and center the map on it if provided and not visible
  const selectCourse = useCallback((course: Course | null) => {
    setSelectedCourse(course);
    
    // If a course is selected and has coordinates, decide whether to center the map
    if (course && course.coordinates) {
      // Get coordinates from course object
      const { lat, lng } = course.coordinates;
      
      // Ensure we have valid coordinates
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        // Check if the course is already visible in the current map bounds
        let isCourseVisible = false;
        
        if (mapBounds) {
          try {
            const coursePosition = new google.maps.LatLng(lat, lng);
            isCourseVisible = mapBounds.contains(coursePosition);
          } catch (error) {
            console.error('Error checking if course is in map bounds:', error);
          }
        }
        
        // Only center the map if the course is NOT already visible
        if (!isCourseVisible) {
          //console.log(`Course not visible in current map view, centering: ${course.name || course.Name}`);
          
          // Merkitse, että keskitys on juuri tehty, jotta radan valintaa ei poisteta heti
          recentlyCenteredRef.current = true;
          
          // Poista keskitystila 2 sekunnin kuluttua
          if (centeringTimeoutRef.current) {
            clearTimeout(centeringTimeoutRef.current);
          }
          
          centeringTimeoutRef.current = setTimeout(() => {
            recentlyCenteredRef.current = false;
          }, 2000);
          
          // Update the center course location state, which will be handled by the map component
          setCenterToCourseLocation({ lat, lng });
        } else {
          //console.log(`Course already visible in map, not centering: ${course.name || course.Name}`);
        }
      }
    }
  }, [mapBounds]);
  
  // Function to clear selected course
  const clearSelectedCourse = useCallback(() => {
    setSelectedCourse(null);
  }, []);
  
  // Muistilippu ensimmäisen ilmoituksen seurantaan
  const hasShownInitialMessage = useRef(false);

  // Lisätään sisäinen viite, joka seuraa milloin keskitys tapahtui
  const recentlyCenteredRef = useRef(false);
  const centeringTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to update map bounds
  const updateMapBounds = useCallback((bounds: google.maps.LatLngBounds) => {
    // Peruutetaan mahdollinen aiempi ajastin
    if (mapBoundsTimeoutRef.current) {
      clearTimeout(mapBoundsTimeoutRef.current);
    }
  
    // Viivytä karttarajojen käsittelyä välttääksemme turhia päivityksiä
    // KUITENKIN ensimmäisellä kerralla toteutetaan päivitys välittömästi
    const isFirstLoad = !isAppReady;
    
    if (isFirstLoad && !hasShownInitialMessage.current) {
      // Ensimmäinen kartan lataus - päivitä välittömästi
      //console.log('CourseContext: Kartta latautunut - merkitään sovellus käyttövalmiiksi');
      setMapBounds(bounds);
      setIsAppReady(true);
      hasShownInitialMessage.current = true; // Merkitään viesti näytetyksi
      lastProcessedBoundsRef.current = bounds;
    } else {
      // Käytä debounce-toimintoa minimoiden turhia päivityksiä
      mapBoundsTimeoutRef.current = setTimeout(() => {
        // Tarkistetaan onko muutos merkittävä
        const shouldUpdate = isSignificantBoundsChange(lastProcessedBoundsRef.current, bounds);
        
        if (shouldUpdate) {
          // Päivitetään karttarajat vain jos muutos on merkittävä
          setMapBounds(bounds);
          lastProcessedBoundsRef.current = bounds;
          
          // Tarkista onko valittu rata karttarajauksen sisällä, ja poista valinta jos ei ole
          // Mutta vain jos karttaa EI ole juuri keskitetty valittuun rataan
          if (selectedCourse && selectedCourse.coordinates && !recentlyCenteredRef.current) {
            try {
              const position = new google.maps.LatLng(
                selectedCourse.coordinates.lat,
                selectedCourse.coordinates.lng
              );
              
              // Jos rata ei ole karttarajauksen sisällä, poista valinta
              if (!bounds.contains(position)) {
                //console.log('Valittu rata jäi karttarajauksen ulkopuolelle, poistetaan valinta');
                setSelectedCourse(null);
              }
            } catch (error) {
              console.error('Virhe tarkistettaessa radan sijaintia:', error);
            }
          }
        }
      }, 500);
    }  
  }, [isAppReady, selectedCourse]);
  
  // Function to clear user data
  const clearUserData = useCallback(() => {
    setSelectedCourse(null);
    setCourseRatings({});
    
    // Clear all rating listeners
    activeRatingListenersRef.current.forEach((unsubscribe) => {
      unsubscribe();
    });
    activeRatingListenersRef.current.clear();
  }, []);
  
  // Function to load ratings for a specific course
  const loadCourseRatings = useCallback((courseId: string) => {
    // Skip if we already have a listener for this course
    if (activeRatingListenersRef.current.has(courseId)) {
      return;
    }
    
    // Set up listener for this course's ratings
    const unsubscribe = CourseRatingService.getInstance().listenForCourseRatings(
      courseId,
      (ratings: CourseRating[]) => {
        // Päivitetään arvostelut tietokannasta saadun datan mukaan
        setCourseRatings(prev => ({
          ...prev,
          [courseId]: ratings
        }));
        
        // Laske ja päivitä keskiarvo ja värikoodi tästä radasta
        if (ratings.length > 0) {
          // Laske keskiarvo, muuntaen värikoodit numeroiksi
          const ratingSum = ratings.reduce((sum, rating) => {
            let numericRating = 0;
            
            if (typeof rating.rating === 'string') {
              // Värikoodimuunnokset
              if (rating.rating === 'green') numericRating = 5;
              else if (rating.rating === 'yellow') numericRating = 3;
              else if (rating.rating === 'red') numericRating = 1;
              // Yritä muuntaa merkkijono numeroksi, jos se on numeerinen
              else {
                const parsed = parseFloat(rating.rating);
                numericRating = isNaN(parsed) ? 0 : parsed;
              }
            } else if (typeof rating.rating === 'number') {
              // Numeerinen arvo suoraan
              numericRating = rating.rating;
            }
            
            // console.log(`Arvostelu ${rating.id}: arvosana=${rating.rating}, numeerinen arvo=${numericRating}`);
            return sum + numericRating;
          }, 0);
          
          const avgRating = ratingSum / ratings.length;
          
          // Päivitä keskiarvo
          setCourseAverageRatings(prev => ({
            ...prev,
            [courseId]: avgRating
          }));
          
          // Määritetään värikoodi keskiarvon perusteella
          const colorRating = 
            avgRating >= 4.0 ? 'green' :
            avgRating >= 3.0 ? 'yellow' : 'red';
          
          setCourseColorRatings(prev => ({
            ...prev,
            [courseId]: colorRating
          }));
        } else {
          // Jos arvosteluja ei ole, poista keskiarvo ja värikoodi
          setCourseAverageRatings(prev => {
            const newState = {...prev};
            delete newState[courseId];
            return newState;
          });
          
          setCourseColorRatings(prev => {
            const newState = {...prev};
            delete newState[courseId];
            return newState;
          });
        }
      }
    );
    
    // Store the unsubscribe function
    activeRatingListenersRef.current.set(courseId, unsubscribe);
  }, []);
  
  // Load ratings for all visible courses
  useEffect(() => {
    // Tallenna viite paikalliseen muuttujaan
    const activeListeners = activeRatingListenersRef.current;
    
    if (visibleCourses.length > 0) {
      // Suodata radat, joiden arvosteluja ei ole vielä ladattu
      const coursesNeedingRatings = visibleCourses.filter(course => 
        course.id && !activeRatingListenersRef.current.has(course.id)
      );
      
      if (coursesNeedingRatings.length > 0) {
        // Ladataan arvostelut näkyville radoille
        coursesNeedingRatings.forEach(course => {
          if (course.id) {
            loadCourseRatings(course.id);
          }
        });
      }
    }
    
    // Cleanup function - käytä tallennettua viitettä (ei .current arvoa)
    return () => {
      activeListeners.forEach((unsubscribe) => {
        unsubscribe();
      });
      activeListeners.clear();
    };
  }, [visibleCourses, loadCourseRatings]);
  
  // Function to clear rating messages
  const clearRatingMessages = useCallback(() => {
    setRatingError(null);
    setRatingSuccess(null);
  }, []);
  
  // Helper function to safely check if Google Maps API is fully loaded
  const isGoogleMapsLoaded = useCallback(() => {
    // First check if the global google object exists
    if (typeof google === 'undefined' || !google.maps) {
      //console.log('Google Maps API not loaded yet - global object not available');
      return false;
    }
    
    // Then verify essential constructors are available
    try {
      // Try creating a simple LatLng object to test if constructors are available
      // This might throw an error if the API is in a partially loaded state
      new google.maps.LatLng(0, 0); // Just testing constructor works, don't need to store result
      return true;
    } catch (error) {
      //('Google Maps API not fully initialized yet - constructors not ready');
      return false;
    }
  }, []);

  // Function to create buffer zone around map bounds (like in V1)
  const createExpandedBounds = useCallback((bounds: google.maps.LatLngBounds): google.maps.LatLngBounds => {
    try {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      
      // Calculate buffer (5% padding like in V1 implementation)
      const latBuffer = (ne.lat() - sw.lat()) * 0.05;
      const lngBuffer = (ne.lng() - sw.lng()) * 0.05;
      
      const expandedNE = new google.maps.LatLng(ne.lat() + latBuffer, ne.lng() + lngBuffer);
      const expandedSW = new google.maps.LatLng(sw.lat() - latBuffer, sw.lng() - lngBuffer);
      
      return new google.maps.LatLngBounds(expandedSW, expandedNE);
    } catch (e) {
      console.error('Error creating expanded bounds:', e);
      // Return the original bounds if we can't create an expanded version
      return bounds;
    }
  }, []);

  // Function to filter courses by bounds with same approach as V1
  const filterCoursesByBounds = useCallback((courses: Course[], bounds: any): Course[] => {
    // Check if we have valid bounds
    if (!bounds) return courses;
    
    // Verify Google Maps API is fully loaded and initialized
    if (!isGoogleMapsLoaded()) {
      //console.log('Google Maps API not fully initialized yet, bypassing bounds filtering');
      return courses;
    }
    
    try {
      // Create expanded bounds to prevent flickering when markers are at the edge
      const expandedBounds = createExpandedBounds(bounds);
      
      // Filter courses based on whether they are within the expanded bounds
      return courses.filter(course => {
        if (!course) return false;
        
        try {
          // Handle different coordinate formats (coordinates object or X/Y properties)
          let lat: number | undefined;
          let lng: number | undefined;
          
          if (course.coordinates) {
            lat = course.coordinates.lat;
            lng = course.coordinates.lng;
          } else if (course.Y !== undefined && course.X !== undefined) {
            // Convert to number if needed
            lat = typeof course.Y === 'string' ? parseFloat(course.Y) : course.Y;
            lng = typeof course.X === 'string' ? parseFloat(course.X) : course.X;
          }
          
          // Skip courses without valid coordinates
          if (lat === undefined || lng === undefined) return false;
          
          const position = new google.maps.LatLng(lat, lng);
          return expandedBounds.contains(position);
        } catch (error) {
          console.error('Error checking course position:', error);
          return false;
        }
      });
    } catch (error) {
      console.error('Error filtering courses by bounds:', error);
      return courses; // Return all courses if filtering fails
    }
  }, [createExpandedBounds, isGoogleMapsLoaded]); // Added isGoogleMapsLoaded dependency

  // Function to filter competitions by bounds
  // This function is exported via the context and used by the useMapInteractionsV2 hook
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filterCompetitionsByBounds = useCallback((competitions: Competition[], bounds: google.maps.LatLngBounds): Competition[] => {
    // Use the same expansion logic as for courses
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    // Bounds expansion factor (as a percentage)
    const expansionFactor = 0.05; // 5%
    
    // Calculate the size of the viewport
    const latSpan = ne.lat() - sw.lat();
    const lngSpan = ne.lng() - sw.lng();
    
    // Calculate expanded coordinates
    const northEastLat = ne.lat() + latSpan * expansionFactor;
    const northEastLng = ne.lng() + lngSpan * expansionFactor;
    const southWestLat = sw.lat() - latSpan * expansionFactor;
    const southWestLng = sw.lng() - lngSpan * expansionFactor;
    
    // Create expanded bounds
    const expandedBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(southWestLat, southWestLng),
      new google.maps.LatLng(northEastLat, northEastLng)
    );
    
    //console.log(`Filtering competitions with expanded bounds: NE(${expandedBounds.getNorthEast().lat()}, ${expandedBounds.getNorthEast().lng()}) SW(${expandedBounds.getSouthWest().lat()}, ${expandedBounds.getSouthWest().lng()})`);
    
    // Uncomment if needed for logging
    // const startCount = competitions.length;
    
    // Filter competitions to include only those within the bounds
    const filtered = competitions.filter(comp => {
      // Skip competitions without coordinates
      if (!comp) return false;
      
      // Try different coordinate formats
      let coords: { lat: number; lng: number } | null = null;
      
      // Check for coordinates object
      if (comp.coordinates && 
          typeof comp.coordinates.lat === 'number' && 
          typeof comp.coordinates.lng === 'number') {
        coords = comp.coordinates;
      }
      // Check for lat and lng properties
      else if (comp.lat && comp.lng) {
        coords = {
          lat: typeof comp.lat === 'string' ? parseFloat(comp.lat) : Number(comp.lat),
          lng: typeof comp.lng === 'string' ? parseFloat(comp.lng) : Number(comp.lng)
        };
      }
      // Check for parent course coordinates
      else if (comp.parentCourse && comp.parentCourse.coordinates) {
        coords = comp.parentCourse.coordinates;
      }
      
      // Check if coordinates are within expanded bounds (just like V1)
      try {
        // Make sure coords is not null before using it
        if (!coords) {
          console.warn('No coordinates found for competition:', comp.name || comp.Name);
          return false;
        }
        return expandedBounds.contains(new google.maps.LatLng(coords.lat, coords.lng));
      } catch (e) {
        console.warn('Error checking if bounds contains competition:', comp.name || comp.Name, e);
        return false;
      }
    });
    
    //console.log(`Filtered courses from ${courses.length} to ${filtered.length}`);
    return filtered;
  }, []); // No dependencies needed as this function only depends on input parameters
  
  // Function to filter courses by search query
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterCoursesBySearch = useCallback((courses: Course[], searchQuery: string): Course[] => {
    // Return empty array when search query is too short
    if (!searchQuery || searchQuery.trim().length < 3) return [];
    
    const query = searchQuery.toLowerCase().trim();
    
    return courses.filter(course => {
      const name = (course.name || course.Name || '').toLowerCase();
      return name.includes(query);
    });
  }, []);
  
  // Function to filter competitions by search query
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterCompetitionsBySearch = useCallback((competitions: Competition[], searchQuery: string): Competition[] => {
    // Return the original list when search query is too short (acts as "no search")
    if (!searchQuery || searchQuery.trim().length < 3) return competitions;
    
    const query = searchQuery.toLowerCase().trim();
    
    return competitions.filter(comp => {
      const name = (comp.name || comp.Name || '').toLowerCase();
      return name.includes(query);
    });
  }, []);
  
  // Function to calculate distance to a course
  const calculateDistanceToCourse = useCallback((course: Course): number => {
    if (!userLocation) return Infinity;
    
    // Extract course coordinates
    let courseCoords = { lat: 0, lng: 0 };
    
    if (course.coordinates && typeof course.coordinates.lat === 'number' && typeof course.coordinates.lng === 'number') {
      courseCoords = { lat: course.coordinates.lat, lng: course.coordinates.lng };
    } else if (course.X !== undefined && course.Y !== undefined) {
      courseCoords = {
        lat: typeof course.Y === 'string' ? parseFloat(course.Y) : Number(course.Y),
        lng: typeof course.X === 'string' ? parseFloat(course.X) : Number(course.X)
      };
    }
    
    // Special case for coordinates at (0,0) - likely placeholder values
    if (courseCoords.lat === 0 && courseCoords.lng === 0) {
      return Infinity; // Skip distance calculation for likely invalid coordinates
    }
    
    // Validate coordinates are within valid ranges
    if (isNaN(courseCoords.lat) || isNaN(courseCoords.lng) ||
        Math.abs(courseCoords.lat) > 90 || Math.abs(courseCoords.lng) > 180) {
      console.warn('Invalid coordinates for course:', course.name || course.Name, courseCoords);
      return Infinity;
    }
    
    // Log the coordinates being used for debugging
    console.debug('Calculating distance from', userLocation, 'to', courseCoords, 'for course', course.name || course.Name);
    
    // Calculate distance using LocationService
    const locationService = LocationService.getInstance();
    const distance = locationService.calculateDistance(
      userLocation.lat, 
      userLocation.lng, 
      courseCoords.lat, 
      courseCoords.lng
    );
    
    // Convert from meters to kilometers
    return distance / 1000;
  }, [userLocation]);
  
  // Format distance for display
  const formatDistance = useCallback((distance: number): string => {
    if (distance === Infinity || distance < 0) {
      return '';
    }
    
    // Distance is already in kilometers at this point
    if (distance < 1) {
      return `${Math.round(distance * 1000)} m`;
    }
    
    return `${distance.toFixed(1)} km`;
  }, []);
  
  // Function to update user location
  const updateUserLocation = useCallback((location: { lat: number; lng: number } | null) => {
    if (location) {
      //console.log('Updating to actual user location', location);
      setUserLocation(location);
      setIsUsingActualUserLocation(true); // When location is provided externally, it's actual user location
      setCenterToUserLocation(true); // Auto-center when user location updates
    } else {
      // If null is passed, use default location
      //console.log('Using default location (Helsinki) as fallback in updateUserLocation');
      // Use a literal object instead of referencing DEFAULT_LOCATION
      setUserLocation({ lat: 60.1699, lng: 24.9384 });
      setIsUsingActualUserLocation(false);
    }
  }, []);
  
  // Käytä viitteitä (ref) estämään päällekkäisiä hakupyyntöjä ja operaatioita
  const pendingFetches = React.useRef<Set<string>>(new Set());
  
  // Viite debounce-toiminnolle karttarajojen päivityksessä (luotu jo aiemmin)
  // Viimeisimmät karttarajat (luotu jo aiemmin)

  // Function to fetch hole count for a course layout
  const fetchLayoutHoleCount = useCallback(async (layoutId: string, forceRefresh: boolean = false): Promise<void> => {
    // Skip if the layout is already loading
    if (loadingLayouts[layoutId] && !forceRefresh) return;
    
    // Skip if the layout hole count is already known and we're not forcing a refresh
    if (layoutHoleCounts[layoutId] !== undefined && !forceRefresh) return;
    
    // NEW: Skip data fetching if the marker is in a cluster
    if (clusteredMarkerIds.includes(layoutId)) {
      console.log(`Skipping hole count fetch for clustered layout: ${layoutId}`);
      return;
    }
    
    try {
      // Varmista että ID on olemassa
      if (!layoutId) {
        console.warn('CourseContext: Yritetään hakea väylämäärää tyhjälle layout ID:lle');
        return;
      }
      
      // Haetaan vain tarvittaessa jos ei pakoteta päivitystä
      // Tämä estää turhia API-kutsuja ja parantaa suorituskykyä
      if (!forceRefresh && layoutHoleCounts[layoutId] > 0) {
        return;
      }
      
      // OPTIMOITU VERSIO: Käytä sekä loadingLayouts-tilaa että pending-viitettä
      // Varmistaa että samalle layoutille ei tehdä useita pyyntöjä (myöskään rinnakkaisesti)
      if (loadingLayouts[layoutId] || pendingFetches.current.has(layoutId)) {
        //console.log(`CourseContext: Layout ${layoutId} on jo latautumassa, ohitetaan uusi haku`);
        return;
      }
      
      // Lisää layout heti pendingFetches-listaan estääkseen samanaikaiset kutsut
      pendingFetches.current.add(layoutId);
      
      // Aseta layoutin lataustila - käytetään functional update -metodia
      // päivitysoperaation atomicisuuden varmistamiseksi
      setLoadingLayouts(prev => ({ ...prev, [layoutId]: true }));
      
      // Hae radan tiedot käyttäen Metrix API:a (ei Firestorea!)
      const metrixCourseDetailService = MetrixCourseDetailService.getInstance();
      const holeCount = await metrixCourseDetailService.fetchCourseHoleCount(layoutId);
      
      // Päivitä tila jos saatiin kelvollinen väylämäärä
      if (holeCount !== null && holeCount > 0) {
        // Käytä funktiota olemassa olevan tilan päivittämiseen
        // Tämä on turvallisempi tapa päivittää tilaa, erityisesti asynkronisissa operaatioissa
        setLayoutHoleCounts(prevCounts => {
          const newCounts = { ...prevCounts, [layoutId]: holeCount };
          // console.log(`CourseContext: Päivitettiin väylämäärä: ${layoutId} => ${holeCount}`);
          // Välimuistia käytetään parantamaan käyttäjäkokemusta
          try {
            // Tallenna välimuistiin väylämäärät
            const cacheKey = `ratahaukka_layout_holes_${layoutId}`;
            localStorage.setItem(cacheKey, JSON.stringify({
              count: holeCount,
              timestamp: Date.now()
            }));
          } catch (e) {
            console.warn('CourseContext: Välimuistiin tallentaminen epäonnistui:', e);
          }
          return newCounts;
        });
      } else {
        // Päivitä nolla-arvo tilaan
        setLayoutHoleCounts(prevCounts => ({
          ...prevCounts,
          [layoutId]: 0  // Käytä nollaa oletusarvona kun väylämäärää ei saatu
        }));
      }
    } catch (error) {
      // Error handling without log
    } finally {
      // Poista layout pendingFetches-listasta ja päivitä lataustila
      pendingFetches.current.delete(layoutId);
      setLoadingLayouts(prev => ({ ...prev, [layoutId]: false }));
    }
  }, [layoutHoleCounts, loadingLayouts, clusteredMarkerIds]);

  // isOnVisibleCourse-funktio poistettu, koska sitä ei enää tarvita
  // Väylätietojen latauslogiikka ei enää riipu kurssin näkyvyydestä kartalla
  
  // Lataa väylätiedot vain kartalla näkyville layouteille
  const loadVisibleLayoutHoleCounts = useCallback(async (layoutIds: string[]) => {
    // Jos hakuprosessi on jo käynnissä, älä käynnistä uutta
    if (isProcessingBatch.current) {
      return;
    }
    
    // Merkitse haku käynnissä olevaksi
    isProcessingBatch.current = true;
    
    try {
      // Rajoitetaan API-kutsujen määrää, että ei ylikuormiteta APIa
      const BATCH_SIZE = 5;
      const batch = layoutIds.slice(0, BATCH_SIZE);
      
      if (batch.length === 0) {
        return;
      }
      
      // Batch processing for layout hole counts
      
      // Suoritetaan haut peräkkäin, ei rinnakkain
      for (let i = 0; i < batch.length; i++) {
        const layoutId = batch[i];
        // Varmistetaan että aiempi haku on valmis ennen seuraavaa
        await fetchLayoutHoleCount(layoutId);
        // Pieni viive hakujen välillä (100ms)
        if (i < batch.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    } finally {
      // Varmista että haku merkitään valmiiksi myös virhetilanteissa
      isProcessingBatch.current = false;
    }
  }, [fetchLayoutHoleCount]);
  
  // Tämä on ENSISIJAINEN hakujen käynnistäjä - kaikki väylähakupyynnöt kulkevat tämän kautta
  // Aktivoi väylätietojen haun VASTA kun visibleCourses on päivitetty kartan perusteella
  useEffect(() => {
    // Varmistaa, että kartta on ladattu ja näkyvissä olevat radat on päivitetty
    if (isAppReady && mapBounds && visibleCourses.length > 0) {
      // Näkyvistä radoista on nyt tehty suodatus kartan perusteella
      if (!shouldLoadHoleCounts) {
        // Activate hole count loading
        setShouldLoadHoleCounts(true);
      }
    }
  }, [isAppReady, mapBounds, visibleCourses, shouldLoadHoleCounts]);
  
  // Function to calculate total hole count for a course
  const getTotalHoleCount = useCallback((course: Course): number => {
    // Jos kyseessä ei ole päärata (Type = 1), palauta vain kyseisen kurssin reikämäärä
    if (course.Type !== '1' || !course.id) {
      return course.id && typeof layoutHoleCounts[course.id] === 'number' ? layoutHoleCounts[course.id] : 0;
    }

    // Haetaan layoutit joko kurssin omasta layouts-attribuutista tai courses-taulukosta
    let uniqueLayouts: Course[] = [];
    
    // TARKISTETAAN ENSIN ONKO KURSSIN OMISSA LAYOUTS-ATTRIBUUTEISSA DATAA
    if (course.layouts && Array.isArray(course.layouts) && course.layouts.length > 0) {
      // Ei lokiteta jokaista hakua - tuottaa liikaa lokitietoja
      // console.log(`🔍 Löytyi ${course.layouts.length} layouttia suoraan radalle ${course.id} (${course.name || course.Name})`);
      uniqueLayouts = course.layouts;
    } else {
      // VAIN JOS LAYOUTS-ATTRIBUUTISTA EI LÖYDY DATAA, ETSITÄÄN COURSES-TAULUKOSTA
      // console.log(`🔍 Etsitään layoutteja courses-taulukosta radalle ${course.id} (${course.name || course.Name})`);
      
      // Optimoidaan hakua suodattamalla ensin Type='2' kurssit
      const potentialLayouts = courses.filter(c => {
        const isTypeLayout = c.Type === '2' || (c.Type && c.Type.toString() === '2');
        if (!isTypeLayout) return false;
        
        // Tarkistetaan ParentID
        if (c.ParentID) {
          return c.ParentID.toString() === course.id?.toString() || c.ParentID === course.id;
        }
        return false;
      });
      
      uniqueLayouts = potentialLayouts;
    }
    
    // Haetaan jokaisen layoutin väylämäärä
    let totalHoles = 0;
    
    // Yksi niistä layouteista, joiden väylämäärää ei vielä ole haettu
    // Käytetään tätä käynnistämään latausjono myöhemmin, jos kartta on valmis
    let layoutsWithoutHoleCounts: string[] = [];
    
    uniqueLayouts.forEach(layout => {
      if (layout.id) {
        // Tarkista ensin onko reikämäärä suoraan saatavilla course-objektista
        const holeCount = layout.holes || layout.Holes || 0;
        if (holeCount > 0) {
          totalHoles += holeCount;
        } else if (typeof layoutHoleCounts[layout.id] === 'number') {
          // Jos ei ole, tarkista onko se layoutHoleCounts-objektissa
          totalHoles += layoutHoleCounts[layout.id];
        } else {
          // Tämä layoutin väylämäärää ei ole vielä haettu
          // Lisätään jono odottavista layouteista
          layoutsWithoutHoleCounts.push(layout.id);
        }
      }
    });
    
    // KRIITTINEN MUUTOS: Käytämme useEffectiä hakemaan väylämäärät viiveellä selaustilanteessa
    // Tämä estää React-virheen "Cannot update a component while rendering a different component"
    if (layoutsWithoutHoleCounts.length > 0) {
      // TÄRKEÄÄ: Sen sijaan että kutsumme loadVisibleLayoutHoleCounts suoraan,
      // käytämme setTimeout:ia siirtämään kutsun seuraavaan event loopiin.
      // Tämä estää React-virheen, koska tila ei päivity kesken renderöinnin.
      setTimeout(() => {
        loadVisibleLayoutHoleCounts(layoutsWithoutHoleCounts);
      }, 0);
    }
    
    return totalHoles;
  }, [layoutHoleCounts, courses, loadVisibleLayoutHoleCounts]);
  
  // Default location (Helsinki) coordinates to use as fallback
  // Using useMemo to prevent unnecessary re-renders when object is used as dependency
  const DEFAULT_LOCATION = useMemo(() => ({ lat: 60.1699, lng: 24.9384 }), []);
  
  // Function to request and update user's geolocation without blocking app initialization
  const requestUserLocation = useCallback(() => {
    let hasResponded = false;
    
    // Helper to use default location
    const applyDefaultLocation = () => {
      if (hasResponded) return;
      hasResponded = true;
      
      // Explicitly set default location to Helsinki
      const defaultLocation = { lat: 60.1699, lng: 24.9384 };
      //console.log('Applying default location (Helsinki):', defaultLocation);
      setUserLocation(defaultLocation);
      setIsUsingActualUserLocation(false);
      setCenterToUserLocation(true); // Still center on this location
      setIsLocationLoading(false);
      
      // Mark app as ready even when using default location
      if (!isAppReady) {
        setIsAppReady(true);
      }
    };
    
    // Guarantee app will proceed after MAX_WAIT_TIME regardless of geolocation status
    const MAX_WAIT_TIME = 8000; // 8 seconds
    const forceDefaultTimeout = setTimeout(() => {
      //console.log('Geolocation taking too long, forcing default location');
      applyDefaultLocation();
    }, MAX_WAIT_TIME);
    
    if (navigator.geolocation) {
      setIsLocationLoading(true);
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Clean up the force default timeout
          clearTimeout(forceDefaultTimeout);
          
          if (hasResponded) return;
          hasResponded = true;
          
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          //console.log('Received user location:', userPos);
          setUserLocation(userPos);
          // Mark that we're using actual user location, not default
          setIsUsingActualUserLocation(true);
          setCenterToUserLocation(true);
          setIsLocationLoading(false);
          
          // Mark app as ready now that we have user location
          if (!isAppReady) {
            setIsAppReady(true);
          }
        },
        (error) => {
          // Clean up the force default timeout
          clearTimeout(forceDefaultTimeout);
          
          if (hasResponded) return;
          hasResponded = true;
          
          //console.log('Geolocation permission denied or error, continuing with default location', error);
          applyDefaultLocation();
        },
        // Shorter timeout - use 3 seconds here since we have an 8-second backup timeout
        { timeout: 3000, maximumAge: 60000, enableHighAccuracy: false }
      );
    } else {
      // Clean up the force default timeout
      clearTimeout(forceDefaultTimeout);
      
      //console.log('Geolocation is not supported by this browser');
      applyDefaultLocation();
    }
  }, [setUserLocation, setCenterToUserLocation, setIsLocationLoading, isAppReady, setIsAppReady]);
  
  // Function to calculate average rating
  const calculateAverageRating = useCallback((ratings: CourseRating[]): number => {
    if (!ratings || ratings.length === 0) return 0;
    
    const sum = ratings.reduce((total, rating) => {
      let value = 0;
      if (rating.rating === 'green') value = 3;
      else if (rating.rating === 'yellow') value = 2;
      else if (rating.rating === 'red') value = 1;
      return total + value;
    }, 0);
    
    return sum / ratings.length;
  }, []);
  
  // Submit a new course rating
  const submitCourseRating = useCallback(async (courseId: string, rating: 'green' | 'yellow' | 'red', comment: string): Promise<void> => {
    if (!user) {
      setRatingError('Sinun täytyy kirjautua sisään arvioidaksesi rataa');
      return;
    }
    
    setLoadingRatings(true);
    setRatingError(null);
    setRatingSuccess(null);
    
    try {
      await CourseRatingService.getInstance().submitRating(
        courseId,
        rating,
        comment,
        user.uid,
        user.displayName || extractUserNameFromEmail(user.email || '')
      );
      
      setRatingSuccess('Arvio tallennettu onnistuneesti!');
    } catch (error: any) {
      console.error('Error submitting rating:', error);
      setRatingError(`Virhe arvion tallennuksessa: ${error.message}`);
    } finally {
      setLoadingRatings(false);
    }
  }, [user]);
  
  // Delete an existing rating
  const deleteRating = useCallback(async (ratingId: string): Promise<void> => {
    if (!user) {
      setRatingError('Sinun täytyy kirjautua sisään poistaaksesi arvion');
      return;
    }
    
    setLoadingRatings(true);
    setRatingError(null);
    setRatingSuccess(null);
    
    try {
      // Pass only the ratingId as required by the service
      await CourseRatingService.getInstance().deleteRating(ratingId);
      setRatingSuccess('Arvio poistettu onnistuneesti!');
    } catch (error: any) {
      console.error('Error deleting rating:', error);
      setRatingError(`Virhe arvion poistamisessa: ${error.message}`);
    } finally {
      setLoadingRatings(false);
    }
  }, [user]);
  
  // Listen for ratings when a course is selected
  useEffect(() => {
    if (selectedCourse?.id) {
      setLoadingRatings(true);
      
      const ratingService = CourseRatingService.getInstance();
      ratingService.listenForCourseRatings(
        selectedCourse.id,
        (ratings) => {
          setCourseRatings(prev => ({ ...prev, [selectedCourse.id]: ratings }));
          setLoadingRatings(false);
        }
      );
    }
    
    // Clean up listeners when component unmounts or course changes
    return () => {
      if (selectedCourse?.id) {
        CourseRatingService.getInstance().stopListeningForCourseRatings(selectedCourse.id);
      }
    };
  }, [selectedCourse]);
  
  // Clean up all listeners when provider unmounts
  useEffect(() => {
    return () => {
      CourseRatingService.getInstance().stopAllRatingListeners();
    };
  }, []);
  
  // Note: We're using the existing filterCoursesByBounds function defined earlier in the code

  // Create default Helsinki map bounds for initial filtering
  const createDefaultMapBounds = useCallback(() => {
    // Helsinki center coordinates
    const helsinkiCenter = DEFAULT_LOCATION;
    
    // Verify Google Maps is fully loaded before attempting to use it
    if (!isGoogleMapsLoaded()) {
      //console.log('Google Maps API not fully initialized yet, cannot create bounds');
      return null;
    }
    
    try {
      // Create a bounds that covers the Helsinki metropolitan area (approx. 30km radius)
      // This creates a rectangular area around Helsinki for initial course filtering
      return new google.maps.LatLngBounds(
        new google.maps.LatLng(helsinkiCenter.lat - 0.3, helsinkiCenter.lng - 0.5),
        new google.maps.LatLng(helsinkiCenter.lat + 0.3, helsinkiCenter.lng + 0.5)
      );
    } catch (error) {
      console.error('Error creating default map bounds:', error);
      return null;
    }
  }, [DEFAULT_LOCATION, isGoogleMapsLoaded]);

  // Effect to initialize map bounds when Google Maps API becomes available
  useEffect(() => {
    // Create a MutationObserver to watch for when Google Maps script is loaded
    let observer: MutationObserver | null = null;
    let googleMapsCheckInterval: NodeJS.Timeout | null = null;

    const initializeMapWithDefaultLocation = () => {
      if (isGoogleMapsLoaded()) {
        // Google Maps API ready, initializing map bounds
        
        // Create default bounds
        const defaultBounds = createDefaultMapBounds();
        if (defaultBounds) {
          // Successfully created default bounds for Helsinki
          setMapBounds(defaultBounds);
          
          // Filter courses by the new bounds if we have courses loaded
          if (courses.length > 0) {
            try {
              const coursesInHelsinki = filterCoursesByBounds(courses, defaultBounds);
              if (coursesInHelsinki.length > 0) {
                setVisibleCourses(coursesInHelsinki);
              }
            } catch (error) {
              console.error('Error filtering courses with new bounds:', error);
            }
          }
        }
        
        // Clean up the interval and observer since Maps is now loaded
        if (googleMapsCheckInterval) {
          clearInterval(googleMapsCheckInterval);
        }
        if (observer) {
          observer.disconnect();
        }
      }
    };
    
    // Set up an interval to check if Google Maps is loaded every 500ms
    googleMapsCheckInterval = setInterval(() => {
      initializeMapWithDefaultLocation();
    }, 500);
    
    // Also set up a MutationObserver to detect when the script is added to the page
    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          initializeMapWithDefaultLocation();
        }
      }
    });
    
    // Start observing the document for when scripts are added
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    
    // Cleanup function
    return () => {
      if (googleMapsCheckInterval) {
        clearInterval(googleMapsCheckInterval);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [courses, isGoogleMapsLoaded, createDefaultMapBounds, filterCoursesByBounds, setVisibleCourses, setMapBounds]);

  // Load course and competition data on mount
  useEffect(() => {
    const loadCourseData = async () => {
      try {
        setIsDataLoading(true);
        // Log removed: Starting course loading from Firestore
        
        const dataService = CourseDataService.getInstance();
        await dataService.initialize();
        const loadedCourses = dataService.getCourses();
        const loadedCompetitions = dataService.getCompetitions();
        
        // Log removed: Course loading successful
        // Log removed: Course breakdown details
          // totalCourses: loadedCourses.length,
          // withCoordinates: loadedCourses.filter(c => c.coordinates && c.coordinates.lat && c.coordinates.lng).length,
          // layouts: loadedCourses.reduce((acc, c) => acc + (c.layouts ? c.layouts.length : 0), 0)
        // });
        // Log removed: Competition loading stats
        
        setCourses(loadedCourses);
        setCompetitions(loadedCompetitions);
        
        // Use a default location (Helsinki) immediately to ensure the application works
        // even if the user doesn't respond to the location permission prompt
        const helsinkiLocation = { lat: 60.1699, lng: 24.9384 };
        setUserLocation(helsinkiLocation);
        setIsUsingActualUserLocation(false); // Mark that we're using default location
        
        // Try to create default map bounds for Helsinki region
        // Note: This may return null if Google Maps API isn't loaded yet
        const defaultBounds = createDefaultMapBounds();
        
        if (defaultBounds) {
          // Google Maps API is loaded, set map bounds and visible courses
          setMapBounds(defaultBounds);
          
          // Filter courses to only show those within the map bounds
          const coursesInHelsinki = filterCoursesByBounds(loadedCourses, defaultBounds);
          setVisibleCourses(coursesInHelsinki.length > 0 ? coursesInHelsinki : loadedCourses);
        } else {
          // Google Maps API not available yet, show all courses
          // Maps API not ready, showing all courses
          setVisibleCourses(loadedCourses);
        }
        
        // Mark application as ready BEFORE requesting user location
        // This ensures the app UI can load even if location permission dialog is waiting
        if (!isAppReady) {
          // Data loaded successfully - marking application as ready
          setIsAppReady(true);
          setIsDataLoading(false); // Data is loaded, end loading state regardless of location
        }
        
        // Request user location directly - app will continue with default location until/unless permission is granted
        requestUserLocation();
        
      } catch (error) {
        console.error('Failed to load course data:', error);
      } finally {
        setIsDataLoading(false);
      }
    };
    
    loadCourseData();
  }, [isAppReady, createDefaultMapBounds, filterCoursesByBounds, setUserLocation, setMapBounds, DEFAULT_LOCATION, requestUserLocation]); // Updated dependencies
  
  // TÄMÄ ON AINOA VÄYLÄMÄÄRIEN HAKEMISEN KÄYNNISTÄVÄ useEffect
  // Aktivoidaan väylien määrien haku vain kun shouldLoadHoleCounts on asetettu true
  useEffect(() => {
    let isMounted = true; // Estä tilan päivitykset jos komponentti puretaan
    
    // Määritellään async-funktio hakusuoritusta varten
    const fetchHoleCounts = async () => {
      // Keskeytetään haku jos se on jo käynnissä tai jos kartta tai radat ei ole valmiita
      if (isProcessingBatch.current || !shouldLoadHoleCounts || !mapBounds || !isAppReady) {
        return;
      }
      
      // Suodatetaan radat, jotka ovat näkyvissä kartalla
      const coursesInViewport = filterCoursesByBounds(visibleCourses, mapBounds);
      if (coursesInViewport.length === 0) {
        return;
      }
      
      // Kerää kaikki layoutit, joiden väylämäärä pitää hakea
      const layoutsToFetch: string[] = [];
      
      // Käy läpi kaikki näkyvät radat ja kerää niiden layoutit yhteen listaan
      for (const course of coursesInViewport) {
        if (!isMounted) return; // Keskeytetään jos komponentti on purettu
        if (!course || !course.id) continue; // Ohitetaan virheelliset radat
        
        // Lisää pääradan ID listaan jos sitä ei ole vielä haettu
        if (!loadingLayouts[course.id] && 
            !pendingFetches.current.has(course.id) &&
            layoutHoleCounts[course.id] === undefined) {
          layoutsToFetch.push(course.id);
        }
        
        // Kerää myös layoutit, jos niitä on
        if (course.layouts && Array.isArray(course.layouts)) {
          // Rajoitetaan 3 layoutiin per rata API:n ylikuormituksen välttämiseksi
          const limitedLayouts = course.layouts.slice(0, 3);
          
          for (const layout of limitedLayouts) {
            if (!layout || !layout.id) continue; // Ohitetaan virheelliset layoutit
            
            // Lisää vain jos layoutia ei ole jo haettu tai hakuprosessi ei ole käynnissä
            if (!loadingLayouts[layout.id] && 
                !pendingFetches.current.has(layout.id) &&
                layoutHoleCounts[layout.id] === undefined) {
              layoutsToFetch.push(layout.id);
            }
          }
        }
      }
      
      // Jos on haettavia layoutteja, käynnistä haku
      if (layoutsToFetch.length > 0) {
        // Lähetä korkeintaan 5 layouttia kerralla hakuun
        await loadVisibleLayoutHoleCounts(layoutsToFetch);
      }
    };
    
    // Käynnistä hakuprosessi vain jos shouldLoadHoleCounts on true
    if (shouldLoadHoleCounts && !isProcessingBatch.current) {
      fetchHoleCounts();
    }
    
    // Cleanup-funktio kun komponentti puretaan
    return () => {
      isMounted = false;
    };
  // Dependency array sisältää vain välttämättömät muuttujat, mikä vähentää turhia suorituskertoja
  }, [mapBounds, visibleCourses, filterCoursesByBounds, layoutHoleCounts, loadingLayouts, 
      fetchLayoutHoleCount, shouldLoadHoleCounts, isAppReady, loadVisibleLayoutHoleCounts, clusteredMarkerIds]);
  
  // Yhdistetty useEffect kartan rajoille ja käyttäjän sijainnille
  useEffect(() => {
    if (courses.length === 0) {
      return;
    }
    
    // Update visible courses based on map bounds or user location
    
    // Ensisijaisesti käytä kartan rajoja jos saatavilla
    if (mapBounds) {
      // Etsi radat kartan rajojen sisältä
      const coursesInBounds = filterCoursesByBounds(courses, mapBounds);
      // Process courses found within map bounds
      
      // Jos rajojen sisällä on ratoja, näytä ne JA PYSÄYTÄ TÄSSÄ
      if (coursesInBounds.length > 0) {
        setVisibleCourses(coursesInBounds);
        return;
      }
      
      // Jos rajojen sisällä ei ole ratoja ja käyttäjän sijainti on tiedossa,
      // näytä lähimmät radat käyttäjän sijainnin perusteella
      if (userLocation && coursesInBounds.length === 0) {
        // No courses in bounds, use user location instead
      }
    }
    
    // Jos kartan rajoja ei ole tai niiden sisällä ei ole ratoja, käytä käyttäjän sijaintia
    if (userLocation) {
      // Show nearest courses based on user location
      
      try {
        // Suodatetaan ensin pois radat, joilla ei ole valideja koordinaatteja
        const validCourses = courses.filter(course => {
          const courseCoords = {
            lat: course.coordinates?.lat || Number(course.Y) || 0,
            lng: course.coordinates?.lng || Number(course.X) || 0
          };
          return courseCoords.lat !== 0 && courseCoords.lng !== 0 && 
                 !isNaN(courseCoords.lat) && !isNaN(courseCoords.lng) &&
                 Math.abs(courseCoords.lat) <= 90 && Math.abs(courseCoords.lng) <= 180;
        });
        
        // Laske etäisyys jokaiselle validille radalle
        const coursesWithDistances = validCourses.map(course => {
          const dist = calculateDistanceToCourse(course);
          return { course, distance: dist };
        }).sort((a, b) => {
          // Järjestä etäisyyden mukaan
          return a.distance - b.distance;
        });
        
        // Näytetään kaikki radat - ei rajoiteta määrää
        // Käyttäjä voi zoomata ulos nähdäkseen enemmän ratoja
        const nearestCourses = coursesWithDistances
          .map(item => item.course);
          
        // Found nearest courses based on user location
        setVisibleCourses(nearestCourses);
        return;
      } catch (e) {
        console.error('CourseContext: Virhe etäisyyden laskennassa:', e);
      }
    }
    
    // Fallback: Jos kartan rajoja tai käyttäjän sijaintia ei ole saatavilla
    // tai niiden perusteella ei löydetty ratoja, näytä kaikki radat
    // Fallback - showing all courses
    setVisibleCourses(courses);
  }, [courses, userLocation, mapBounds, calculateDistanceToCourse, filterCoursesByBounds]);
    
  
  // Create context value object with all the state and functions
  const contextValue: CourseContextType = {
    // Layout hole count state
    layoutHoleCounts,
    loadingLayouts,
    fetchLayoutHoleCount,
    loadVisibleLayoutHoleCounts,
    getTotalHoleCount,
    
    // NEW: Clustered markers tracking
    clusteredMarkerIds,
    setClusteredMarkerIds,
    updateClusteredMarkers: (newClusteredMarkers: string[]) => {
      setClusteredMarkerIds(newClusteredMarkers);
    },
    isMarkerClustered: (markerId: string) => clusteredMarkerIds.includes(markerId),
    
    // Course state
    courses,
    visibleCourses,
    setVisibleCourses,
    selectedCourse,
    selectCourse,
    setSelectedCourse,
    clearSelectedCourse,
    
    // Course filtering
    filterCoursesByBounds,
    filterCoursesBySearch,
    
    // Competition/Event filtering
    visibleCompetitions,
    setVisibleCompetitions,
    filterCompetitionsByBounds,
    filterCompetitionsBySearch,
    eventDateRange,
    setEventDateRange,
    eventShowPdgaOnly,
    setEventShowPdgaOnly,
    
    // Location functionality
    userLocation,
    setUserLocation,
    updateUserLocation,
    calculateDistanceToCourse,
    formatDistance,
    isLocationLoading,
    requestUserLocation,
    centerToUserLocation,
    setCenterToUserLocation,
    isUsingActualUserLocation,
    centerToCourseLocation,
    setCenterToCourseLocation,
    
    // Map bounds and navigation
    mapBounds,
    updateMapBounds,
    mapZoom,
    updateMapZoom,
    
    // User data management
    clearUserData,
    
    // Course ratings
    courseRatings,
    courseAverageRatings,
    courseColorRatings,
    loadingRatings,
    loadCourseRatings,
    ratingError,
    ratingSuccess,
    submitCourseRating,
    deleteRating,
    clearRatingMessages,
    calculateAverageRating,
    
    // Course data
    competitions,
    isDataLoading
  };
  
  return (
    <CourseContext.Provider value={contextValue}>
      {children}
    </CourseContext.Provider>
  );
};

// Custom hook for accessing the course context
export function useCourseContext(): CourseContextType {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error('useCourseContext must be used within a CourseProvider');
  }
  return context;
};
